import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { FormField } from '@/components/ui/FormField';
import { Card } from '@/components/ui/Card';
import { useUserInfo, useUpdateUser, type UserRole } from '@/api/hooks/useUsers';
import { ApiException } from '@/api/client';

export function UserEditPage() {
  const { id = '' } = useParams<{ id: string }>();
  const decodedId = decodeURIComponent(id);
  const navigate = useNavigate();
  const { data, isLoading } = useUserInfo(decodedId);
  const update = useUpdateUser();

  const [userEmail, setUserEmail] = useState('');
  const [userRole, setUserRole] = useState<UserRole>('internal_user');
  const [teamsText, setTeamsText] = useState('');
  const [maxBudget, setMaxBudget] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (data) {
      setUserEmail(data.user_email ?? '');
      setUserRole((data.user_role as UserRole | undefined) ?? 'internal_user');
      setTeamsText(data.teams?.join(', ') ?? '');
      setMaxBudget(data.max_budget != null ? String(data.max_budget) : '');
    }
  }, [data]);

  if (isLoading || !data) {
    return (
      <div
        style={{
          height: 384,
          background: '#e2e8f0',
          borderRadius: 8,
          animation: 'pulse 2s infinite',
        }}
      />
    );
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const teams = teamsText
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    update.mutate(
      {
        user_id: data.user_id,
        user_email: userEmail || undefined,
        user_role: userRole,
        teams: teams.length ? teams : undefined,
        max_budget: maxBudget ? Number(maxBudget) : undefined,
      },
      {
        onSuccess: () => navigate(`/users/${encodeURIComponent(data.user_id)}`),
        onError: (e) =>
          setSubmitError(e instanceof ApiException ? e.message : 'Update failed'),
      },
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 600 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Edit User</h1>
      <Card>
        <form
          onSubmit={handleSubmit}
          style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
        >
          {submitError && (
            <div
              style={{
                padding: 12,
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 6,
                color: '#991b1b',
                fontSize: 13,
              }}
            >
              {submitError}
            </div>
          )}
          <FormField label="Email" htmlFor="user_email">
            <Input
              id="user_email"
              type="email"
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
            />
          </FormField>
          <FormField label="Role" htmlFor="user_role">
            <Select
              id="user_role"
              value={userRole}
              onChange={(e) => setUserRole(e.target.value as UserRole)}
              options={[
                { value: 'internal_user', label: 'Internal User' },
                { value: 'internal_user_viewer', label: 'Internal User (Viewer)' },
                { value: 'proxy_admin', label: 'Proxy Admin' },
                { value: 'proxy_admin_viewer', label: 'Proxy Admin (Viewer)' },
              ]}
            />
          </FormField>
          <FormField label="Teams" htmlFor="teams">
            <Input
              id="teams"
              value={teamsText}
              onChange={(e) => setTeamsText(e.target.value)}
            />
          </FormField>
          <FormField label="Max Budget" htmlFor="max_budget">
            <Input
              id="max_budget"
              type="number"
              value={maxBudget}
              onChange={(e) => setMaxBudget(e.target.value)}
            />
          </FormField>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button type="button" variant="outline" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}